import { v4 as uuid } from 'uuid';
import initializeReferralTab from './referralTab';

const STORAGE_KEY = 'referral-context-v1';
let memoryContext = null;
let storageUnavailable = false;

/** Read the pending context without requiring browser storage for checkout. */
export function readReferralContext() {
  if (storageUnavailable) return memoryContext;
  let stored;
  try {
    initializeReferralTab();
    stored = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return memoryContext;
  }
  try {
    const context = JSON.parse(stored);
    if (
      context?.version === 1 &&
      typeof context.slug === 'string' &&
      context.slug.length > 0 &&
      typeof context.visitId === 'string' &&
      /^[0-9a-f-]{36}$/i.test(context.visitId) &&
      typeof context.confirmed === 'boolean' &&
      (context.discountCode === null || typeof context.discountCode === 'string')
    )
      return context;
  } catch {
    // Malformed storage must not revive stale in-memory attribution.
  }
  memoryContext = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    storageUnavailable = true;
  }
  return null;
}

/** Synchronize confirmed attribution, including when the SDK loads after lookup. */
export function syncReferralAnalytics() {
  const context = readReferralContext();
  try {
    if (context?.confirmed) window.mixpanel?.register?.({ 'Referral Link': context.slug });
    else window.mixpanel?.unregister?.('Referral Link');
  } catch {
    // Analytics must not interrupt navigation or purchase creation.
  }
}

/** Replace the tab record and its analytics property together. */
function writeContext(context) {
  memoryContext = context;
  try {
    if (context) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    else window.sessionStorage.removeItem(STORAGE_KEY);
    storageUnavailable = false;
  } catch {
    storageUnavailable = true;
  }
  syncReferralAnalytics();
  return context;
}

/** Reuse the visit identifier only when entering the same pending shortlink. */
export function captureReferralContext(slug) {
  const previous = readReferralContext();
  return writeContext({
    version: 1,
    slug,
    visitId: previous?.slug === slug ? previous.visitId : uuid(),
    confirmed: false,
    discountCode: null,
  });
}

/** Ignore lookup results for a shortlink that has already been replaced. */
export function confirmReferralContext(context, discountCode) {
  if (readReferralContext()?.visitId !== context.visitId) return;
  writeContext({
    ...context,
    confirmed: true,
    discountCode: typeof discountCode === 'string' ? discountCode : null,
  });
}

/** Clear only the context acknowledged by the current operation. */
export function clearReferralContext(context = readReferralContext()) {
  if (context && readReferralContext()?.visitId !== context.visitId) return;
  writeContext(null);
}

/** Preserve explicit coupon semantics and use referral discounts after manual coupons. */
export function referralCoupon(explicitCoupon, manualCoupon, context) {
  if (explicitCoupon !== undefined && explicitCoupon !== null) return explicitCoupon;
  if (manualCoupon?.trim()) return manualCoupon;
  return context?.discountCode || manualCoupon;
}
